import { useLocation } from "react-router-dom";
import { AGENT_AREAS, ORG_AREA } from "@/config/nav";

export type AgentArea = "receivables" | "payables" | "expenses" | "books" | "organisation" | null;

function matchesPath(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** Area routes with no nav item (OAuth callbacks, legacy aliases); nav pages are derived from the nav config. */
const EXTRA_AREA_PATHS: Record<Exclude<AgentArea, null>, string[]> = {
  receivables: [],
  payables: ["/settings/ap", "/oauth/gmail"],
  expenses: [],
  books: [],
  organisation: ["/organisation"],
};

/**
 * Built from the nav config: an area missing one of its pages would collapse its sidebar section on landing there.
 * Pages come before their area's root so, on an equal prefix, the sort (stable) keeps the one with a page label.
 */
const AREA_PREFIXES: Array<{ area: Exclude<AgentArea, null>; prefix: string; pageLabel?: string }> = [
  ...AGENT_AREAS.flatMap((area) =>
    area.kind === "live"
      ? [
          ...area.pages.map((page) => ({ area: area.id, prefix: page.to, pageLabel: page.label })),
          { area: area.id, prefix: area.to },
        ]
      : [],
  ),
  ...ORG_AREA.pages.map((page) => ({ area: ORG_AREA.id, prefix: page.to, pageLabel: page.label })),
  { area: ORG_AREA.id, prefix: ORG_AREA.to },
  ...Object.entries(EXTRA_AREA_PATHS).flatMap(([area, paths]) =>
    paths.map((prefix) => ({ area: area as Exclude<AgentArea, null>, prefix })),
  ),
].sort((a, b) => b.prefix.length - a.prefix.length);

const AREA_LABEL: Record<Exclude<AgentArea, null>, string> = {
  receivables: "Receivables",
  payables: "Payables",
  expenses: "Expenses",
  books: "Books",
  organisation: "Organisation",
};

/** `pageLabel` is the nav label of the page the path sits under, null for a route with no nav item. */
export function useAgentArea(): { area: AgentArea; label: string | null; pageLabel: string | null } {
  const { pathname } = useLocation();
  // Longest prefix wins, so a page nested under another area's route (say
  // /settings/payments vs /settings/integrations/gmail) resolves to its own area.
  for (const { area, prefix, pageLabel } of AREA_PREFIXES) {
    if (matchesPath(pathname, prefix)) return { area, label: AREA_LABEL[area], pageLabel: pageLabel ?? null };
  }
  return { area: null, label: null, pageLabel: null };
}
